import { AfterViewInit, Component, ElementRef, Input, OnInit } from "@angular/core";
import { HTMLService } from "@services/htmlService.service";
import { TranslationService } from "@services/translation.service";
import { IVeriTableBindings, IVeriTableRow } from "../veriTable.component";
import "./veriTableSummary.component.scss";

export interface IEventSummary<T = any> {
    id: string;
    label: string;
    subtitle: string;
    count: number;
    events: T[];
    icon?: string;
    color?: string;
    data?: any;
}

export interface IEventSummaryConfiguration {
    icon: string;
}

@Component({
    selector: "veri-table-summary",
    template: require("./veriTableSummary.component.html"),
})

export class VeriTableSummaryComponent implements OnInit, AfterViewInit {

    @Input() loading: boolean = false;
    @Input() maxEvents: number;

    childTable: IVeriTableBindings<any>;
    eventSummary: IEventSummary<IVeriTableRow>[] = [];
    selectedEvent: IEventSummary<IVeriTableRow> = null;

    totalPlaceholdersRequired: number;

    componentLoaded = false;

    constructor(
        private readonly elementRef: ElementRef,
        private readonly translationService: TranslationService,
        private readonly htmlService: HTMLService) { }

    ngOnInit(): void {
        if (!this.maxEvents) {
            const boxWidth = 150 + 10;
            this.maxEvents = Math.ceil(this.elementRef.nativeElement.getBoundingClientRect().width / boxWidth);
        }
    }

    ngAfterViewInit() {

        this.componentLoaded = true;

        var nearest = this.htmlService.findNearest(this.elementRef.nativeElement, "veri-table");
        var childTable = this.htmlService.getAngularJSComponentFromElement<IVeriTableBindings>(nearest);
        this.registerChild(childTable);
    }

    registerChild(veriTable: IVeriTableBindings<any>) {
        this.childTable = veriTable;
        this.childTable.veriTableContainer = this;

        this.onRowsUpdated();
    }

    onRowsUpdated() {
        this.selectedEvent = null;
        this.childTable.setFilterRows(null);

        this.updateNumbers()
    }

    updateNumbers() {

        if (this.childTable && this.childTable.headers && this.childTable.headers.some(h => h.includeInSummary)) {
            this.buildEventSummary(this.childTable.displayRows);
        }
    }

    onEventSummaryClick(event: IEventSummary<any>) {

        if (!this.childTable) {
            return;
        }

        if (this.selectedEvent && this.selectedEvent.id === event.id) {
            this.selectedEvent = null;
            this.childTable.setFilterRows(null);

            return;
        }

        this.selectedEvent = event;
        this.childTable.setFilterRows(this.selectedEvent.events);
    }

    trackByEventSummary(index: number, item: IEventSummary) {
        return item.id;
    }

    //------

    private buildEventSummary(rows: IVeriTableRow[]) {

        this.eventSummary = [];

        let indexes: number[] = [];

        this.childTable.headers.forEach((header, index) => {
            if (header.includeInSummary) {
                indexes.push(index);
            }
        })


        indexes.forEach(index => {

            var headerTitle = this.childTable.headers[index].title;

            rows.forEach(row => {
                const cell = row.cells[index];

                if (!cell || !cell.contents || cell.excludeFromSummary) {
                    return;
                }

                let eventLabel = cell.summaryLabel || cell.contents;

                var existingEventSummary = this.eventSummary.find(event => event.label == eventLabel);
                if (existingEventSummary) {
                    existingEventSummary.count++;
                }
                else {
                    existingEventSummary = {
                        id: eventLabel,
                        label: eventLabel,
                        subtitle: cell.summarySubLabel || headerTitle,
                        count: 1,
                        icon: cell.summaryIcon,
                        events: [],
                        color: cell.summaryColour || row.eventSummaryColour
                    };

                    this.eventSummary.push(existingEventSummary);
                }

                existingEventSummary.events.push(row);
            });

            this.eventSummary.sort((x, y) => y.count - x.count);

            if (this.maxEvents && this.eventSummary.length > this.maxEvents) {
                var popped = this.eventSummary.splice(this.maxEvents - 1);
                var poppedCount = popped.reduce((a, b) => a + b.count, 0);

                this.eventSummary.push({
                    id: "other",
                    count: poppedCount,
                    label: this.translationService.t("veriTableContainer.other"),
                    subtitle: headerTitle,
                    icon: "help",
                    events: popped.map(a => { return a.events; }).reduce((a, b) => a.concat(b))
                });

                this.eventSummary.sort((x, y) => y.count - x.count);
            }

            this.totalPlaceholdersRequired = this.eventSummary?.length;

            const defaultPlacedHolderCount: number = 3;
            if (this.eventSummary?.length == 0) {

                this.totalPlaceholdersRequired = Math.min(this.maxEvents, this.eventSummary.length);

                if (this.totalPlaceholdersRequired == 0) {
                    this.totalPlaceholdersRequired = defaultPlacedHolderCount;
                }
            }
        })
    }
}
